"use client";

import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { v4 as uuidv4 } from "uuid";
import {
  Plus,
  Users,
  Sparkles,
  Save,
  X,
  ChevronDown,
  Zap,
  Clock,
} from "lucide-react";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  AudienceCriteria,
  AudienceType,
  FilterGroup,
  FilterGroupLogic,
  AudiencePreviewData,
} from "../../types/audienceTypes";
import FilterGroupCard from "../FilterGroup/FilterGroupCard";
import AudiencePreviewPanel from "../AudiencePreview/AudiencePreviewPanel";
import FilterCategoryModal from "../FilterCategorySelector/FilterCategoryModal";

// ============================================================================
// Styles
// ============================================================================

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  .audience-builder {
    --ab-bg-primary: #0f172a;
    --ab-bg-secondary: #1e293b;
    --ab-bg-tertiary: #334155;
    --ab-bg-card: #1e293b;
    --ab-border: #475569;
    --ab-border-hover: #64748b;
    --ab-text-primary: #f8fafc;
    --ab-text-secondary: #94a3b8;
    --ab-text-muted: #64748b;
    --ab-accent: #f97316;
    --ab-accent-hover: #fb923c;
    --ab-accent-glow: rgba(249, 115, 22, 0.3);
    --ab-success: #22c55e;
    --ab-info: #3b82f6;
    --ab-and-color: #8b5cf6;
    --ab-or-color: #06b6d4;

    font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
    background: var(--ab-bg-primary);
    min-height: 100vh;
  }

  .audience-builder * {
    box-sizing: border-box;
  }

  .ab-mono {
    font-family: 'JetBrains Mono', monospace;
  }

  .ab-card {
    background: var(--ab-bg-card);
    border: 1px solid var(--ab-border);
    border-radius: 12px;
    transition: all 0.2s ease;
  }

  .ab-card:hover {
    border-color: var(--ab-border-hover);
  }

  .ab-glow {
    box-shadow: 0 0 40px var(--ab-accent-glow);
  }

  .ab-input {
    background: var(--ab-bg-primary);
    border: 1px solid var(--ab-border);
    color: var(--ab-text-primary);
    transition: all 0.2s ease;
  }

  .ab-input:focus {
    border-color: var(--ab-accent);
    box-shadow: 0 0 0 3px var(--ab-accent-glow);
    outline: none;
  }

  .ab-input::placeholder {
    color: var(--ab-text-muted);
  }

  .ab-logic-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 12px;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .ab-logic-and {
    background: rgba(139, 92, 246, 0.15);
    color: #a78bfa;
    border: 1px solid rgba(139, 92, 246, 0.3);
  }

  .ab-logic-and:hover {
    background: rgba(139, 92, 246, 0.25);
    border-color: rgba(139, 92, 246, 0.5);
  }

  .ab-logic-or {
    background: rgba(6, 182, 212, 0.15);
    color: #22d3ee;
    border: 1px solid rgba(6, 182, 212, 0.3);
  }

  .ab-logic-or:hover {
    background: rgba(6, 182, 212, 0.25);
    border-color: rgba(6, 182, 212, 0.5);
  }

  .ab-count-display {
    background: linear-gradient(135deg, var(--ab-accent) 0%, #ea580c 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .ab-pulse {
    animation: ab-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  @keyframes ab-pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  .ab-connector {
    position: relative;
  }

  .ab-connector::before {
    content: '';
    position: absolute;
    left: 50%;
    top: -16px;
    height: 16px;
    width: 2px;
    background: var(--ab-border);
  }

  .ab-connector::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -16px;
    height: 16px;
    width: 2px;
    background: var(--ab-border);
  }

  .ab-pattern-bg {
    background-image:
      radial-gradient(circle at 1px 1px, var(--ab-border) 1px, transparent 0);
    background-size: 24px 24px;
  }

  .ab-gradient-border {
    position: relative;
    background: var(--ab-bg-card);
    border-radius: 12px;
  }

  .ab-gradient-border::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 13px;
    background: linear-gradient(135deg, var(--ab-accent), var(--ab-info));
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .ab-gradient-border:hover::before {
    opacity: 1;
  }

  .ab-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .ab-scrollbar::-webkit-scrollbar-track {
    background: var(--ab-bg-primary);
    border-radius: 3px;
  }

  .ab-scrollbar::-webkit-scrollbar-thumb {
    background: var(--ab-border);
    border-radius: 3px;
  }

  .ab-scrollbar::-webkit-scrollbar-thumb:hover {
    background: var(--ab-border-hover);
  }
`;

// ============================================================================
// Component Props
// ============================================================================

interface AudienceBuilderProps {
  initialCriteria?: AudienceCriteria;
  onSave?: (criteria: AudienceCriteria, name: string) => void;
  onCancel?: () => void;
  onPreviewUpdate?: (preview: AudiencePreviewData) => void;
}

// ============================================================================
// Main Component
// ============================================================================

export function AudienceBuilder({
  initialCriteria,
  onSave,
  onCancel,
  onPreviewUpdate,
}: AudienceBuilderProps) {
  // State
  const [audienceName, setAudienceName] = useState("Untitled Audience");
  const [audienceType, setAudienceType] = useState<AudienceType>("active");
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(
    initialCriteria?.filterGroups || []
  );
  const [groupLogic, setGroupLogic] = useState<FilterGroupLogic>(
    initialCriteria?.groupLogic || "AND"
  );
  const [isAddFilterModalOpen, setIsAddFilterModalOpen] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<AudiencePreviewData>({
    totalCount: 0,
    isProcessing: false,
    breakdown: [],
    sampleContacts: [],
  });

  // Mock preview data update (replace with actual API call)
  useEffect(() => {
    const totalFilters = filterGroups.reduce(
      (acc, group) => acc + group.filters.length,
      0
    );

    if (totalFilters > 0) {
      setPreviewData((prev) => ({ ...prev, isProcessing: true }));

      // Simulate API call
      const timer = setTimeout(() => {
        const mockCount = Math.floor(Math.random() * 500) + 50;
        const newPreview: AudiencePreviewData = {
          totalCount: mockCount,
          isProcessing: false,
          breakdown: [
            {
              field: "trade",
              label: "By Trade",
              segments: [
                { label: "HVAC", count: Math.floor(mockCount * 0.4), percentage: 40 },
                { label: "Plumbing", count: Math.floor(mockCount * 0.3), percentage: 30 },
                { label: "Electrical", count: Math.floor(mockCount * 0.2), percentage: 20 },
                { label: "Roofing", count: Math.floor(mockCount * 0.1), percentage: 10 },
              ],
            },
            {
              field: "role",
              label: "By Role",
              segments: [
                { label: "Technician", count: Math.floor(mockCount * 0.45), percentage: 45 },
                { label: "Sales", count: Math.floor(mockCount * 0.25), percentage: 25 },
                { label: "Manager", count: Math.floor(mockCount * 0.2), percentage: 20 },
                { label: "Owner", count: Math.floor(mockCount * 0.1), percentage: 10 },
              ],
            },
          ],
          sampleContacts: [
            { id: "1", name: "John Smith", email: "john@acmehvac.com", company: "Acme HVAC" },
            { id: "2", name: "Sarah Johnson", email: "sarah@coolairpros.com", company: "Cool Air Pros" },
            { id: "3", name: "Mike Williams", email: "mike@plumbperfect.com", company: "Plumb Perfect" },
          ],
        };
        setPreviewData(newPreview);
        onPreviewUpdate?.(newPreview);
      }, 800);

      return () => clearTimeout(timer);
    } else {
      setPreviewData({
        totalCount: 0,
        isProcessing: false,
        breakdown: [],
        sampleContacts: [],
      });
    }
  }, [filterGroups, onPreviewUpdate]);

  // Handlers
  const handleAddFilterGroup = useCallback(() => {
    const newGroup: FilterGroup = {
      id: uuidv4(),
      logic: "AND",
      filters: [],
    };
    setFilterGroups((prev) => [...prev, newGroup]);
    setActiveGroupId(newGroup.id);
    setIsAddFilterModalOpen(true);
  }, []);

  const handleAddFilterToGroup = useCallback((groupId: string) => {
    setActiveGroupId(groupId);
    setIsAddFilterModalOpen(true);
  }, []);

  const handleRemoveFilterGroup = useCallback((groupId: string) => {
    setFilterGroups((prev) => prev.filter((g) => g.id !== groupId));
  }, []);

  const handleUpdateFilterGroup = useCallback((updatedGroup: FilterGroup) => {
    setFilterGroups((prev) =>
      prev.map((g) => (g.id === updatedGroup.id ? updatedGroup : g))
    );
  }, []);

  const handleCloneFilterGroup = useCallback((groupId: string) => {
    setFilterGroups((prev) => {
      const groupToClone = prev.find((g) => g.id === groupId);
      if (!groupToClone) return prev;

      const clonedGroup: FilterGroup = {
        ...groupToClone,
        id: uuidv4(),
        filters: groupToClone.filters.map((f) => ({ ...f, id: uuidv4() })),
      };

      const index = prev.findIndex((g) => g.id === groupId);
      return [...prev.slice(0, index + 1), clonedGroup, ...prev.slice(index + 1)];
    });
  }, []);

  const handleFilterSelected = useCallback(
    (fieldId: string) => {
      if (!activeGroupId) return;

      setFilterGroups((prev) =>
        prev.map((group) => {
          if (group.id !== activeGroupId) return group;
          return {
            ...group,
            filters: [
              ...group.filters,
              {
                id: uuidv4(),
                fieldId,
                operator: "is_any_of",
                value: null,
              },
            ],
          };
        })
      );
      setIsAddFilterModalOpen(false);
    },
    [activeGroupId]
  );

  const toggleGroupLogic = useCallback(() => {
    setGroupLogic((prev) => (prev === "AND" ? "OR" : "AND"));
  }, []);

  const handleSave = useCallback(() => {
    const criteria: AudienceCriteria = {
      type: audienceType,
      groupLogic,
      filterGroups,
    };
    onSave?.(criteria, audienceName);
  }, [audienceType, groupLogic, filterGroups, audienceName, onSave]);

  const totalFilters = filterGroups.reduce(
    (acc, group) => acc + group.filters.length,
    0
  );

  return (
    <>
      <style>{styles}</style>

      <div className="audience-builder min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-50 border-b border-[var(--ab-border)] bg-[var(--ab-bg-primary)]/95 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              {/* Left: Name & Type */}
              <div className="flex items-center gap-4 flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--ab-accent)] to-orange-600 flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <input
                    className="ab-input text-xl font-semibold bg-transparent border-none px-0 focus:ring-0 text-[var(--ab-text-primary)] w-64"
                    onChange={(e) => setAudienceName(e.target.value)}
                    placeholder="Untitled Audience"
                    type="text"
                    value={audienceName}
                  />
                </div>

                {/* Audience Type Toggle */}
                <div className="flex items-center gap-2 ml-4">
                  <button
                    className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
                      audienceType === "active"
                        ? "bg-[var(--ab-accent)]/20 text-[var(--ab-accent)] border border-[var(--ab-accent)]/30"
                        : "text-[var(--ab-text-muted)] hover:text-[var(--ab-text-secondary)]"
                    )}
                    onClick={() => setAudienceType("active")}
                  >
                    <Zap className="w-3.5 h-3.5" />
                    Active
                  </button>
                  <button
                    className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
                      audienceType === "static"
                        ? "bg-[var(--ab-info)]/20 text-[var(--ab-info)] border border-[var(--ab-info)]/30"
                        : "text-[var(--ab-text-muted)] hover:text-[var(--ab-text-secondary)]"
                    )}
                    onClick={() => setAudienceType("static")}
                  >
                    <Clock className="w-3.5 h-3.5" />
                    Static
                  </button>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-3">
                <Button
                  className="text-[var(--ab-text-secondary)] hover:text-[var(--ab-text-primary)] border-[var(--ab-border)]"
                  onClick={onCancel}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-[var(--ab-accent)] hover:bg-[var(--ab-accent-hover)] text-white"
                  disabled={totalFilters === 0}
                  onClick={handleSave}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Audience
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Filter Builder */}
            <div className="lg:col-span-2 space-y-6">
              {/* Empty State */}
              {filterGroups.length === 0 && (
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="ab-card ab-pattern-bg p-12 text-center"
                  initial={{ opacity: 0, y: 20 }}
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[var(--ab-accent)]/20 to-orange-600/20 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-[var(--ab-accent)]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[var(--ab-text-primary)] mb-2">
                    Build Your Audience
                  </h3>
                  <p className="text-[var(--ab-text-secondary)] mb-6 max-w-md mx-auto">
                    Add filters to segment your contacts. Combine multiple criteria
                    with AND/OR logic to create the perfect audience.
                  </p>
                  <Button
                    className="bg-[var(--ab-accent)] hover:bg-[var(--ab-accent-hover)] text-white"
                    onClick={handleAddFilterGroup}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Filter
                  </Button>
                </motion.div>
              )}

              {/* Filter Groups */}
              {/* @ts-ignore - framer-motion type incompatibility with React 18 */}
              <AnimatePresence mode="popLayout">
                {filterGroups.map((group, index) => (
                  <React.Fragment key={group.id}>
                    {/* Logic Connector Between Groups */}
                    {index > 0 && (
                      <motion.div
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center justify-center py-2"
                        exit={{ opacity: 0, scale: 0.8 }}
                        initial={{ opacity: 0, scale: 0.8 }}
                      >
                        <div className="flex-1 h-px bg-[var(--ab-border)]" />
                        <button
                          className={cn(
                            "ab-logic-pill mx-4 ab-connector",
                            groupLogic === "AND" ? "ab-logic-and" : "ab-logic-or"
                          )}
                          onClick={toggleGroupLogic}
                        >
                          {groupLogic}
                        </button>
                        <div className="flex-1 h-px bg-[var(--ab-border)]" />
                      </motion.div>
                    )}

                    {/* Filter Group Card */}
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20, scale: 0.95 }}
                      initial={{ opacity: 0, y: 20 }}
                      layout
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    >
                      <FilterGroupCard
                        group={group}
                        groupNumber={index + 1}
                        onAddFilter={() => handleAddFilterToGroup(group.id)}
                        onClone={() => handleCloneFilterGroup(group.id)}
                        onRemove={() => handleRemoveFilterGroup(group.id)}
                        onUpdate={handleUpdateFilterGroup}
                      />
                    </motion.div>
                  </React.Fragment>
                ))}
              </AnimatePresence>

              {/* Add Filter Group Button */}
              {filterGroups.length > 0 && (
                <motion.div
                  animate={{ opacity: 1 }}
                  className="flex justify-center"
                  initial={{ opacity: 0 }}
                >
                  <Button
                    className="text-[var(--ab-text-secondary)] hover:text-[var(--ab-text-primary)] border-[var(--ab-border)] border-dashed hover:border-[var(--ab-accent)] hover:bg-[var(--ab-accent)]/5"
                    onClick={handleAddFilterGroup}
                    variant="outline"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Filter Group
                  </Button>
                </motion.div>
              )}
            </div>

            {/* Right: Preview Panel */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <AudiencePreviewPanel
                  filterCount={totalFilters}
                  preview={previewData}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Filter Category Modal */}
        <FilterCategoryModal
          isOpen={isAddFilterModalOpen}
          onClose={() => setIsAddFilterModalOpen(false)}
          onSelectField={handleFilterSelected}
        />
      </div>
    </>
  );
}

export default AudienceBuilder;
